import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import Footer from '../components/shared/Footer';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      if (result.user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-col md:flex-row flex-grow">
      {/* Left Side - Image and Welcome Text (visible on all screens) */}
      <div 
        className="w-full md:w-1/2 relative min-h-[45vh] md:min-h-screen flex flex-col"
        style={{ backgroundColor: '#1a1a1a' }}
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/5970790/pexels-photo-5970790.jpeg')`,
            filter: 'brightness(0.85)'
          }}
        />
        
        {/* Overlay Gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)'
          }}
        />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between h-full p-6 md:p-12">
          {/* Spacer for top */}
          <div className="flex-grow"></div>
          
          {/* Welcome Text */}
          <div className="text-white mb-6 md:mb-12">
            <div className="flex items-start">
              <div 
                className="w-1 h-24 md:h-32 mr-3 md:mr-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: '#82C43C' }}
              />
              <div>
                <h1 className="text-3xl md:text-5xl font-bold leading-tight">
                  Bienvenue
                </h1>
                <h2 className="text-2xl md:text-4xl font-bold leading-tight">
                  dans votre
                </h2>
                <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                  espace client.
                </h2>
              </div>
            </div>
          </div>
          
          {/* Bottom Logo */}
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_fd99e742-6c59-4267-85f6-7d5b0b735f5d/artifacts/qzmj7w6w_IMG_0229.jpeg" 
              alt="Fortuneo" 
              className="h-12 md:h-16 object-contain"
            />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-start py-8 md:py-16 px-6 bg-white min-h-[55vh] md:min-h-screen">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6 md:mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_fd99e742-6c59-4267-85f6-7d5b0b735f5d/artifacts/qzmj7w6w_IMG_0229.jpeg" 
            alt="Fortuneo" 
            className="h-16 md:h-20 object-contain"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-6 md:mb-8 px-4">
          <p className="text-base md:text-lg" style={{ color: '#333333' }}>
            Saisissez votre
          </p>
          <p className="text-base md:text-lg font-semibold" style={{ color: '#82C43C' }}>
            identifiant et mot de
          </p>
          <p className="text-base md:text-lg font-semibold" style={{ color: '#82C43C' }}>
            passe
          </p>
        </div>

        {/* Loading spinner */}
        {isLoading && (
          <div className="mb-4">
            <div 
              className="w-8 h-8 md:w-10 md:h-10 border-4 border-gray-200 rounded-full animate-spin"
              style={{ borderTopColor: '#82C43C' }}
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {/* Identifiant Field */}
          <div 
            className="rounded-xl p-4"
            style={{ 
              backgroundColor: '#F5F5F5',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <label 
              htmlFor="username" 
              className="block text-sm font-medium mb-2"
              style={{ color: '#82C43C' }}
            >
              Identifiant
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent border-none outline-none text-base"
              style={{ color: '#333333' }}
              placeholder="000000"
              required
              data-testid="username-input"
            />
          </div>

          {/* Mot de passe Field */}
          <div 
            className="rounded-xl p-4"
            style={{ 
              backgroundColor: '#F5F5F5',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <label 
              htmlFor="password" 
              className="block text-sm font-medium mb-2"
              style={{ color: '#82C43C' }}
            >
              Mot de passe
            </label>
            <div className="flex items-center">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-base"
                style={{ color: '#333333' }}
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="ml-2 p-1 hover:opacity-70 transition-opacity"
                style={{ color: '#999999' }}
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Mémoriser l'identifiant */}
          <div className="flex items-center px-1">
            <input
              type="checkbox"
              id="remember"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
              style={{ accentColor: '#82C43C' }}
              data-testid="remember-checkbox"
            />
            <label 
              htmlFor="remember" 
              className="ml-2 text-sm"
              style={{ color: '#666666' }}
            >
              Mémoriser l&apos;identifiant
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="rounded-lg px-4 py-3 text-sm text-center"
              style={{ 
                backgroundColor: '#FEE2E2', 
                color: '#DC2626',
                border: '1px solid #FECACA'
              }}
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          {/* Connexion Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-full text-white font-semibold text-base transition-all duration-200 hover:opacity-90"
            style={{ 
              backgroundColor: isLoading ? '#A3D977' : '#82C43C',
              boxShadow: '0 4px 6px rgba(130, 196, 60, 0.3)'
            }}
            data-testid="login-button"
          >
            {isLoading ? 'Connexion...' : 'Connexion'}
          </button>
        </form>

        {/* Spacer */}
        <div className="flex-grow"></div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
